// @ts-strict-ignore
import React, {
  useEffect, useMemo, useState, useCallback, useContext,
} from 'react';
import {
  Box,
  Button,
  ButtonVariant,
  Modal,
  Text,
  useSnackBar,
} from '@usealloy/component-library';
import dayjs from 'dayjs';

import { updateThreshold } from 'flow/store/redux/slice';
import { selectFocusedAction, selectPublicVersion } from '~/dashboard/views/workflows/store/redux/selectors';
import { UserContext } from '~/dashboard/context';
import { selectThresholdSimParamsByPublicId } from '~/flow/thresholdSimulator/redux/selectors';
import SaveVersionModal from 'flow/editor/components/MenuBar/MenuBarModals/ValidateAndSaveVersionModals/SaveVersionModal';
import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';
import apiCalls from '~/helpers/apiCalls';
import log from '~/helpers/logging';

import RuleCard from './components/RuleCard';
import ThresholdsCard from './components/ThresholdsCard';
import {
  distillChartLevelsFromBreakdownData,
} from './components/ThresholdsCard/helpers';
import {
  findCorrectPercentileByThresholdValueAndOperator,
  getEvaluationPercentileByThresholdOperator,
  getModalTitleText,
  getParsedThresholdOperator,
} from './helpers';
import { SaveRequiredModal } from './components/ThresholdsCard/Modals/SaveRequiredModal';
import { DataLoadingModal } from './components/ThresholdsCard/Modals/DataLoadingModal';
import {
  selectThresholdSimInitialPopulatedThreshold,
  selectThresholdSimPopulatedThreshold,
  selectThresholdSimType,
} from 'flow/store/redux/selectors/workflowFeatures';
import { DEFAULT_SELECTED_RANGE } from './constants';


type ThresholdSimulatorModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

const getModalHeader = (modalTitleDetails: string) => (
  <Text size={4} weight={500} style={{ lineHeight: 'var(--cl-line-height-4' }}>
    Threshold Simulator:{' '}
    <span style={{ color: 'var(--cl-gray-700)' }}>{modalTitleDetails}</span>
  </Text>
);

const ThresholdSimulatorModal: React.FC<ThresholdSimulatorModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { typhonDomain } = useContext(UserContext);
  const addSnackBar = useSnackBar();
  const dispatch = useAppDispatch();
  const publicVersion = useAppSelector(selectPublicVersion);

  const populatedThreshold = useAppSelector(selectThresholdSimPopulatedThreshold);
  const initialPopulatedThreshold = useAppSelector(selectThresholdSimInitialPopulatedThreshold);
  const thresholdType = useAppSelector(selectThresholdSimType);
  const reduxParams = useAppSelector(
    selectThresholdSimParamsByPublicId(populatedThreshold.publicId),
  );

  const focusedAction = useAppSelector(selectFocusedAction);
  const modalTitleDetails = getModalTitleText(focusedAction);
  const modalHeader = getModalHeader(modalTitleDetails);

  // Date variables (filtering/fetching)
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedDates, setSelectedDates] = useState([]);
  // Graph variables
  const [breakdownData, setBreakdownData] = useState(null);
  const [isBreakdownLoading, setIsBreakdownLoading] = useState(null);
  const [hasGraphError, setHasGraphError] = useState(null);
  const [hasRequiredSaveError, setHasRequiredSaveError] = useState(null);
  const [isSaveVersionModalOpen, setIsSaveVersionModalOpen] = useState(false);
  // Persisting new values
  const [newThresholdValue, setNewThresholdValue] = useState(null);
  const [manuallyEditedValue, setManuallyEditedValue] = useState(null);
  const [isManuallyEditing, setIsManuallyEditing] = useState(false);

  const thresholdOperator = getParsedThresholdOperator(populatedThreshold);

  useEffect(() => {
    // @ts-expect-error error type
    if (reduxParams?.error?.status === 404) setHasRequiredSaveError(true);
  }, [reduxParams]);

  useEffect(() => {
    // TODO:  track original bounds and prevent users from selecting bad date ranges?
    //        would be useful for tracking valid date range selection
    if (reduxParams?.validStartDate && reduxParams?.validEndDate) {
      let startMoment = dayjs.utc(reduxParams.validStartDate);
      const endMoment = dayjs.utc(reduxParams.validEndDate);

      // NOTE: if there is a big range in selectable dates, default to a small range
      if (endMoment.diff(startMoment, 'day') > DEFAULT_SELECTED_RANGE) {
        startMoment = endMoment.subtract(DEFAULT_SELECTED_RANGE, 'day');
      }

      const startDateFormatted = startMoment.format('YYYY-MM-DD');
      const endDateFormatted = endMoment.format('YYYY-MM-DD');

      setStartDate(startDateFormatted);
      setEndDate(endDateFormatted);
      setSelectedDates([startDateFormatted, endDateFormatted].map(dayjs));
    }
  }, [
    reduxParams?.validStartDate,
    reduxParams?.validEndDate,
  ]);

  useEffect(() => {
    if (selectedDates && selectedDates[0] && selectedDates[1]) {
      const [startDateObj, endDateObj] = selectedDates;
      let startDateTmp = startDate;
      let endDateTmp = endDate;
      if (startDateObj?.$d) {
        startDateTmp = dayjs(startDateObj.$d.toUTCString()).format('YYYY-MM-DD');
      }
      if (endDateObj?.$d) {
        endDateTmp = dayjs(endDateObj.$d.toUTCString()).format('YYYY-MM-DD');
      }
      setStartDate(startDateTmp);
      setEndDate(endDateTmp);
    }
  }, [selectedDates, endDate, startDate]);

  const getThresholdSimBreakdown = useCallback(async () => {
    if (populatedThreshold.publicId && publicVersion && startDate && endDate) {
      setIsBreakdownLoading(true);
      setHasGraphError(null);
      try {
        const response = await apiCalls.workflows.thresholdSimulator.getBreakdown({
          typhonDomain,
          publicId: populatedThreshold.publicId,
          workflowVersion: publicVersion,
          startDate,
          endDate,
        });
        setBreakdownData(response);
        // TODO: percentile approximation? would need to rework how that works
      } catch (exception) {
        setBreakdownData(null);
        setHasGraphError(true);
        log('error', 'GET /threshold-simulator/breakdown/ exception', {}, exception);
      } finally {
        setIsBreakdownLoading(false);
      }
    }
  }, [populatedThreshold.publicId, publicVersion, startDate, endDate, typhonDomain]);
  useEffect(() => { getThresholdSimBreakdown(); }, [getThresholdSimBreakdown]);

  const isLoading = reduxParams?.isLoading || isBreakdownLoading;

  const breakdownMap = useMemo(
    () => new Map<number, number>(breakdownData?.data?.breakdown),
    [breakdownData?.data?.breakdown],
  );
  const chartLevels = useMemo(
    () => distillChartLevelsFromBreakdownData(
      breakdownMap,
      thresholdOperator,
      Number(populatedThreshold.right.attributeValue),
    ),
    [breakdownMap, populatedThreshold, thresholdOperator],
  );

  const currPercentile = useMemo(
    () => findCorrectPercentileByThresholdValueAndOperator({
      breakdownMap,
      chartLevels,
      targetThreshold: Number(initialPopulatedThreshold.right.attributeValue),
      thresholdOperator,
    }),
    [breakdownMap, chartLevels, initialPopulatedThreshold, thresholdOperator],
  );
  const newPercentile = useMemo(
    () => findCorrectPercentileByThresholdValueAndOperator({
      breakdownMap,
      chartLevels,
      targetThreshold: Number(populatedThreshold.right.attributeValue),
      thresholdOperator,
    }),
    [breakdownMap, chartLevels, populatedThreshold, thresholdOperator],
  );
  const [sliderPercentile, setSliderPercentile] = useState(newPercentile);
  useEffect(() => { setSliderPercentile(newPercentile); }, [newPercentile]);

  const evaluationPercentile = useMemo(() => getEvaluationPercentileByThresholdOperator({
    breakdownMap,
    chartLevels,
    sliderPercentile,
    thresholdOperator,
  }), [breakdownMap, chartLevels, sliderPercentile, thresholdOperator]);
  const totalEvaluations = breakdownData?.data?.total_evaluations;
  const getNumOfEvaluations = percentile => {
    if (!percentile) return null;
    return Math.round(totalEvaluations * (percentile / 100));
  };

  const onCloseModal = () => {
    onClose();
    const percentile = findCorrectPercentileByThresholdValueAndOperator({
      breakdownMap,
      chartLevels,
      targetThreshold: Number(populatedThreshold.right.attributeValue),
      thresholdOperator,
    });
    setSliderPercentile(percentile);
    setNewThresholdValue(null);
    setStartDate(null);
    setEndDate(null);
  };
  const closeButton = <Button onClick={onCloseModal}>Cancel</Button>;

  useEffect(() => {
    let percentile = sliderPercentile;
    if (thresholdOperator === 'lt') {
      percentile = sliderPercentile + 1;
    } else if (thresholdOperator === 'gt') {
      percentile = sliderPercentile - 1;
    }
    setNewThresholdValue(breakdownMap.get(percentile));
  }, [breakdownMap, sliderPercentile, thresholdOperator]);

  const onSaveNewThresholdValue = eventValue => {
    setIsManuallyEditing(false);
    const newValue = Number(eventValue);
    setNewThresholdValue(newValue);
    const updatedPercentile = findCorrectPercentileByThresholdValueAndOperator({
      breakdownMap,
      chartLevels,
      targetThreshold: newValue,
      thresholdOperator,
    });
    setSliderPercentile(updatedPercentile);
  };

  const onManualThresholdValueChange = eventValue => {
    if (!isManuallyEditing) {
      setIsManuallyEditing(true);
    }
    setManuallyEditedValue(Number(eventValue));
  };

  const onApplyThreshold = () => {
    try {
      dispatch(updateThreshold({
        path: `${populatedThreshold.publicId}.right.attributeValue`,
        value: newThresholdValue,
      }));
      dispatch(updateThreshold({
        path: `${populatedThreshold.publicId}.operator`,
        value: populatedThreshold.operator,
      }));
      addSnackBar({
        duration: 2_000,
        id: 'threshold-simulator-update-success',
        title: 'Successfully updated threshold',
        type: 'success',
      });
    } catch (e) {
      addSnackBar({
        duration: 2_000,
        id: 'threshold-simulator-update-error',
        title: 'Could not update threshold value',
        type: 'danger',
      });
    }
    onCloseModal();
  };
  const applyThresholdButton = (
    <Button
      onClick={onApplyThreshold}
      variant={ButtonVariant.primary}
      disabled={newThresholdValue === null
        || newThresholdValue === undefined
        || isNaN(newThresholdValue)}
    >
      Apply Threshold
    </Button>
  );

  if (isLoading) return <DataLoadingModal isOpen={isOpen} onClose={onClose} />;

  if (hasRequiredSaveError) {
    return (
      <>
        <SaveRequiredModal
          isOpen={isOpen}
          onClose={onClose}
          saveVersion={() => {
            onClose();
            setIsSaveVersionModalOpen(true);
          }}
        />
        <SaveVersionModal
          isOpen={isSaveVersionModalOpen}
          onClose={() => setIsSaveVersionModalOpen(false)}
        />
      </>
    );
  }

  return (
    <Modal
      header={modalHeader}
      isOpen={isOpen}
      onClose={onClose}
      primaryAction={applyThresholdButton}
      secondaryAction={closeButton}
      size="large"
      autoFocus={false}
    >
      <Box direction="column" gap={5}>
        <Box gap={5}>
          <RuleCard
            title="Current Rule"
            threshold={initialPopulatedThreshold}
            evaluationPercentile={currPercentile}
            numOfEvaluations={getNumOfEvaluations(currPercentile)}
            thresholdType={thresholdType}
            thresholdValue={Number(initialPopulatedThreshold.right.attributeValue)}
            totalEvaluations={totalEvaluations}
            newThresholdValue={undefined}
            onManualThresholdValueChange={undefined}
            manuallyEditedValue={undefined}
            isManuallyEditing={undefined}
          />
          <RuleCard
            title="New Rule"
            threshold={populatedThreshold}
            evaluationPercentile={evaluationPercentile}
            numOfEvaluations={getNumOfEvaluations(evaluationPercentile)}
            thresholdType={thresholdType}
            thresholdValue={newThresholdValue}
            totalEvaluations={totalEvaluations}
            newThresholdValue={newThresholdValue}
            onSaveNewThresholdValue={onSaveNewThresholdValue}
            onManualThresholdValueChange={onManualThresholdValueChange}
            manuallyEditedValue={manuallyEditedValue}
            isManuallyEditing={isManuallyEditing}
            isEditable={false}
          />
        </Box>
        <ThresholdsCard
          breakdownMap={breakdownMap}
          chartLevels={chartLevels}
          isLoading={isLoading}
          hasError={Boolean(hasGraphError || reduxParams?.error)}
          selectedDates={selectedDates}
          setSelectedDates={setSelectedDates}
          setSliderPercentile={setSliderPercentile}
          sliderPercentile={sliderPercentile}
          thresholdOperator={thresholdOperator}
          thresholdType={thresholdType}
        />
      </Box>
    </Modal>
  );
};

export default ThresholdSimulatorModal;
